export type PublicGuideSurface = "airport_end" | "walk_end";
export type AdChannel = "appsInToss" | "web";
export type AdChoice = "unknown" | "granted" | "denied";
export type WebProviderState = "PREPARING" | "READY" | "NEEDS_ATTENTION";

export type GuideAdDecisionInput = {
  route: string;
  channel: AdChannel;
  surface: PublicGuideSurface;
  userRequested: boolean;
  choice: AdChoice;
  productionActivated: boolean;
  adGroupId: string | null;
  webProviderState: WebProviderState;
  sessionImpressions: number;
  lastImpressionAt: number | null;
  now: number;
};

export const GUIDE_AD_SESSION_KEY = "japangolmok.ads.public-guide.session";
export const GUIDE_AD_LAST_IMPRESSION_KEY = "japangolmok.ads.public-guide.last-impression";
export const GUIDE_AD_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

export function publicGuideSurfaceForRoute(route: string): PublicGuideSurface | null {
  if (/^\/airports\/[a-z]{3}\/?$/i.test(route)) return "airport_end";
  if (/^\/walks\/[a-z0-9-]+\/?$/i.test(route)) return "walk_end";
  return null;
}

export function canRequestGuideAd(input: GuideAdDecisionInput): { allowed: true } | { allowed: false; reason: string } {
  if (publicGuideSurfaceForRoute(input.route) !== input.surface) return { allowed: false, reason: "route_prohibited" };
  if (!input.userRequested) return { allowed: false, reason: "explicit_request_required" };
  if (input.choice !== "granted") return { allowed: false, reason: "choice_required" };
  if (!input.productionActivated) return { allowed: false, reason: "provider_inactive" };
  if (input.channel === "web") {
    if (input.webProviderState !== "READY") return { allowed: false, reason: "adsense_site_not_ready" };
    // No AdSense slot/code exists. Keep this false until provider approval,
    // surface review, CMP and a dedicated web adapter are independently ready.
    return { allowed: false, reason: "web_adapter_unavailable" };
  }
  if (!input.adGroupId) return { allowed: false, reason: "group_id_unconfigured" };
  if (input.sessionImpressions >= 1) return { allowed: false, reason: "session_cap" };
  if (input.lastImpressionAt !== null && input.now - input.lastImpressionAt < GUIDE_AD_COOLDOWN_MS) return { allowed: false, reason: "cooldown" };
  return { allowed: true };
}

export type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;
export function readGuideAdFrequency(session: StorageLike, persistent: StorageLike) {
  const sessionCount = Number(session.getItem(GUIDE_AD_SESSION_KEY) ?? "0");
  const savedAt = sessionCountValue(persistent.getItem(GUIDE_AD_LAST_IMPRESSION_KEY));
  return {
    sessionImpressions: Number.isFinite(sessionCount) && sessionCount > 0 ? sessionCount : 0,
    lastImpressionAt: savedAt,
  };
}
function sessionCountValue(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
export function recordGuideAdImpression(session: StorageLike, persistent: StorageLike, now: number) {
  const current = readGuideAdFrequency(session, persistent);
  session.setItem(GUIDE_AD_SESSION_KEY, String(current.sessionImpressions + 1));
  persistent.setItem(GUIDE_AD_LAST_IMPRESSION_KEY, String(now));
}
export function clearGuideAdFrequency(session: StorageLike, persistent: StorageLike) {
  session.removeItem(GUIDE_AD_SESSION_KEY);
  persistent.removeItem(GUIDE_AD_LAST_IMPRESSION_KEY);
}
